import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import type { CampaignContentKind } from '../../constants/campaign-sequence';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CampaignEntity } from './campaign.entity';

/**
 * PD-17613 — a campaign's copy and scripts: an email (subject A/B and body),
 * a call script, a voicemail, the gatekeeper and turnaround lines, a LinkedIn
 * message. `fk_user__id_owner` null is the campaign's version; set, it is one
 * salesperson's own version of a script, used for their calls instead.
 *
 * New wording never sends until it is approved (`approvedAt`); editing the
 * body clears the approval. Every change is kept in campaign_content_version.
 */
@Index('campaign_content__campaign', ['fkCampaignId', 'stepNo', 'kind'], {})
@Entity('campaign_content', {}, { auditable: true })
export class CampaignContentEntity extends BaseEntity {
	@Column('int', { name: 'fk_campaign__id', unsigned: true })
	public fkCampaignId!: number;

	/** The step it belongs to; null for campaign-wide copy (gatekeeper, turnarounds). */
	@Column('int', { name: 'stepNo', unsigned: true, nullable: true })
	public stepNo!: number | null;

	// App-validated (CampaignContentKind).
	@Column('varchar', { name: 'kind', length: 20 })
	public kind!: CampaignContentKind | string;

	@Column('varchar', { name: 'subjectA', length: 500, nullable: true })
	public subjectA!: string | null;

	@Column('varchar', { name: 'subjectB', length: 500, nullable: true })
	public subjectB!: string | null;

	/** Plain text, one paragraph per blank line. Sent to Instantly as HTML, one `<div>` per line. */
	@Column('text', { name: 'body', nullable: true })
	public body!: string | null;

	@Column('datetime', { name: 'approvedAt', nullable: true })
	public approvedAt!: Date | null;

	@Column('int', { name: 'fk_user__id_approvedBy', unsigned: true, nullable: true })
	public fkUserIdApprovedBy!: number | null;

	/** Null = the campaign's version; set = this salesperson's own version. */
	@Column('int', { name: 'fk_user__id_owner', unsigned: true, nullable: true })
	public fkUserIdOwner!: number | null;

	/** The current version number (campaign_content_version.versionNo). */
	@Column('int', { name: 'versionNo', unsigned: true, default: () => '1' })
	public versionNo!: number;

	@ManyToOne(() => CampaignEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_campaign__id', referencedColumnName: 'id' }])
	public fkCampaign!: CampaignEntity;
}
